/**
 * 代理商认证 API（镜像 customer US-002 契约）
 * 正式路径前缀由 client.AGENT_API_BASE 提供（/agent/api/v1）
 *
 * FE-E2b 薄壳化：五件套模板（login/logout/refresh/me/menus）下沉
 * @shared/api/auth 工厂；本文件保留端配置、代登录对与类型，导出面不变。
 *
 * 红线 RL-001：本模块仅处理 会话/菜单，不展示任何业务敏感数据明细。
 */
import {
  agentGet,
  agentPost,
  clearSession,
  DEFAULT_AGENT_ID,
  loadSession,
  saveSession,
  signOutLocal,
  USE_MOCK,
  type AgentSession,
  type ApiResult,
  type MenuNodeView,
  type PortalSessionView,
} from './client';
import { createAuthApi, type TokenPair } from '@shared/api/auth';

export type { TokenPair, MenuNodeView };

const auth = createAuthApi<AgentSession, PortalSessionView>({
  get: agentGet,
  post: agentPost,
  loadSession,
  saveSession,
  clearSession,
  signOutLocal,
  mockMe: () => {
    const s = loadSession();
    const id = DEFAULT_AGENT_ID;
    return {
      subjectId: id,
      channel: 'AGENT',
      principal: s?.principal || 'agent@mock',
      roles: [],
      permissions: [],
      agentId: id,
      mustChangePassword: s?.me?.mustChangePassword === true,
      mfaSetupRequired: s?.me?.mfaSetupRequired === true,
      otpGraceRemainingSeconds: s?.me?.otpGraceRemainingSeconds ?? 0,
    };
  },
  buildSession: (t: TokenPair, principal: string): AgentSession => ({
    accessToken: t.accessToken,
    refreshToken: t.refreshToken,
    principal,
    expiresAt: t.expiresIn ? Date.now() + t.expiresIn * 1000 : undefined,
  }),
  // 作用域 id 来自 me.agentId（登录后才有；拉取失败保留旧值）
  mergeAfterLogin: (current, meRes, menusRes) => ({
    ...current,
    me: meRes.ok ? meRes.data : null,
    menus: menusRes.ok ? menusRes.data : null,
    agentId: meRes.ok && meRes.data.agentId != null ? meRes.data.agentId : current.agentId,
  }),
  // refreshSessionMe 同步回写作用域 id
  applyMe: (current, me) => ({
    ...current,
    me,
    agentId: me.agentId != null ? me.agentId : current.agentId,
  }),
});

export const { fetchMe, refreshSessionMe, fetchMenus, refreshTokens, logout, login, logoutLocal } = auth;

/** 代登录换会话响应（契约 §0.3；刻意无 refreshToken——代登录会话不可续期 D3） */
export type PortalLoginResponse = {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
  impersonated: boolean;
  principal?: string;
  subjectId?: string;
  agentId?: string;
};

/**
 * 运营代登录票据消费（POST /auth/portal-login，permitAll，skipAuth）。
 * 错误码 3428 / 3429 / 3430 / USER_DISABLED 由落地页分流文案，不走全局错误处理。
 */
export async function portalLogin(ticket: string): Promise<ApiResult<PortalLoginResponse>> {
  return agentPost<PortalLoginResponse>(
    '/auth/portal-login',
    { ticket },
    () => ({
      accessToken: 'mock-access-portal-login',
      tokenType: 'Bearer',
      expiresIn: 3600,
      impersonated: true,
      principal: 'agent@mock',
      subjectId: DEFAULT_AGENT_ID,
      agentId: DEFAULT_AGENT_ID,
    }),
    { skipAuth: true },
  );
}

export { USE_MOCK };
